import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, SprayCan, Droplet, Eraser, Palette, ShoppingBag, Loader2, Plus, Minus, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";

export default function AccessoiresPage() {
    const [accessories, setAccessories] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        api.get("/accessories")
            .then(({ data }) => setAccessories(data || []))
            .catch(() => setAccessories([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="accessoires-page" className="min-h-screen" style={{ background: "var(--bg)" }}>
            {/* HERO */}
            <section className="hero-warm border-b border-[var(--border-soft)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
                    <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Hulpmiddelen &amp; verzorging</p>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-3 leading-[1.05] tracking-tight">
                        Accessoires <span className="font-script" style={{ color: "var(--rose-deep)" }}>voor je sticker.</span>
                    </h1>
                    <p className="mt-6 max-w-xl text-base lg:text-lg leading-relaxed" style={{ color: "var(--text-2)" }}>
                        Voor een vlekkeloos resultaat: applicatietape, glasreiniger, applicatie-spray en lijmverwijderaar.
                        Alles wat je nodig hebt om je sticker als een professional te plakken.
                    </p>
                </div>
            </section>

            {/* PRODUCTEN */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                {loading ? (
                    <div className="py-20 flex justify-center"><Loader2 size={28} className="animate-spin" style={{ color: "var(--rose-deep)" }} /></div>
                ) : accessories.length === 0 ? (
                    <div className="text-center py-16">
                        <p className="text-sm" style={{ color: "var(--text-2)" }}>Momenteel geen accessoires beschikbaar. Kom binnenkort terug!</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="accessoires-grid">
                        {accessories.map(a => (
                            <AccessoryCard key={a.id} accessory={a} user={user} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function AccessoryCard({ accessory, user }) {
    const { fetchCart } = useCart();
    const [qty, setQty] = useState(1);
    const [adding, setAdding] = useState(false);

    const Icon =
        accessory.icon === "tape" ? Package
        : accessory.icon === "spray" ? SprayCan
        : accessory.icon === "cleaner" ? Droplet
        : accessory.icon === "remover" ? Eraser
        : accessory.icon === "color-card" ? Palette
        : Package;

    const add = async () => {
        if (!user) { toast.error("Log eerst in om toe te voegen aan je mandje"); return; }
        setAdding(true);
        try {
            await api.post("/cart/items/accessory", { accessory_id: accessory.id, quantity: qty });
            await fetchCart();
            toast.success(`${accessory.name} toegevoegd aan winkelmandje`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kon niet toevoegen");
        } finally {
            setAdding(false);
        }
    };

    const stockBadge = () => {
        const tracked = accessory.stock_count !== null && accessory.stock_count !== undefined;
        if (!accessory.in_stock) return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest mt-1" style={{ color: "#B8704C" }}><Clock size={10} /> Op bestelling</span>;
        if (!tracked) return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--success)" }}><CheckCircle2 size={10} /> Op voorraad</span>;
        if (accessory.stock_count === 0) return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--rose-deep)" }}>Uitverkocht</span>;
        if (accessory.stock_count <= 3) return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest mt-1" style={{ color: "#B8704C" }}>Nog {accessory.stock_count} {accessory.stock_count === 1 ? "stuk" : "stuks"}</span>;
        return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--success)" }}><CheckCircle2 size={10} /> {accessory.stock_count} op voorraad</span>;
    };

    return (
        <article
            data-testid={`accessory-card-${accessory.slug}`}
            className="card-soft bg-white p-6 flex flex-col gap-4 transition-all hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(62,39,35,0.08)]">
            <div className="flex items-start justify-between gap-3">
                {accessory.image_url ? (
                    <div className="w-24 h-24 rounded-2xl border border-[var(--border-soft)] bg-white overflow-hidden shrink-0">
                        <img src={accessory.image_url} alt={accessory.name} className="w-full h-full object-contain"
                            onError={(e) => { e.target.style.display = "none"; }}
                            data-testid={`accessory-image-${accessory.slug}`} />
                    </div>
                ) : (
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--rose-tint)" }}>
                        <Icon size={22} strokeWidth={1.5} style={{ color: "var(--rose-deep)" }} />
                    </div>
                )}
                <div className="text-right">
                    <p className="font-display text-2xl">€ {(accessory.price || 0).toFixed(2)}</p>
                    {stockBadge()}
                </div>
            </div>
            <div className="flex-1">
                <p className="font-display text-xl leading-tight">{accessory.name}</p>
                <p className="label-tiny mt-1" style={{ color: "var(--text-3)" }}>{accessory.unit}</p>
                <p className="text-sm mt-3" style={{ color: "var(--text-2)" }}>{accessory.short}</p>
            </div>
            <div className="flex items-center gap-2 border-t border-[var(--border-soft)] pt-4">
                <div className="inline-flex items-stretch rounded-full border border-[var(--border)] overflow-hidden">
                    <button data-testid={`acc-qty-minus-${accessory.slug}`} onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 hover:bg-[var(--rose-tint)]"><Minus size={12} /></button>
                    <span className="px-3.5 py-2 font-bold text-xs min-w-[36px] text-center">{qty}</span>
                    <button data-testid={`acc-qty-plus-${accessory.slug}`} onClick={() => setQty(Math.min(50, qty + 1))} className="px-3 hover:bg-[var(--rose-tint)]"><Plus size={12} /></button>
                </div>
                {!user ? (
                    <Link to={`/login?next=${encodeURIComponent("/accessoires")}`}
                        data-testid={`acc-login-${accessory.slug}`}
                        className="flex-1 btn-ghost text-xs">Log in om te kopen</Link>
                ) : (
                    <button data-testid={`acc-add-${accessory.slug}`}
                        onClick={add} disabled={adding || (accessory.stock_count === 0)}
                        className="flex-1 btn-rose text-xs disabled:opacity-60">
                        {adding ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                        {adding ? "…" : "In mandje"}
                    </button>
                )}
            </div>
        </article>
    );
}
